import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  MessageCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Info,
  HelpCircle,
  UserPlus,
  BarChart3,
  Globe,
  Shield,
  Calendar,
  Bell,
  Users,
  Building2,
  Layers,
  Headphones,
  Tags,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { getStoredLegacyToken } from '../services/legacyAuthService';
import { LanguageSelector } from './LanguageSelector';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const CREATOR_REQUEST_BADGE_POLL_MS = 5 * 60_000;

const Sidebar = ({ isCollapsed, onToggle }: SidebarProps) => {
  const location = useLocation();
  const { user, logout, isSuperAdmin } = useAuth();
  const { t } = useTranslation();
  const [pendingCount, setPendingCount] = useState(0);
  const emailLocalPart = String(user?.email || '').toLowerCase().split('@')[0] || '';
  const canManageCreatorRequests = isSuperAdmin;

  useEffect(() => {
    if (!canManageCreatorRequests) return;
    const check = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const rawToken =
          getStoredLegacyToken()
          || localStorage.getItem('legacy_token')
          || sessionStorage.getItem('legacy_token')
          || '';
        const token = rawToken
          ? (/^Bearer\s+/i.test(rawToken) ? rawToken : `Bearer ${rawToken}`)
          : '';
        const resp = await fetch('/api/creator-registration/list?status=pending', {
          headers: token
            ? { Authorization: token, Accept: 'application/json' }
            : { Accept: 'application/json' },
        });
        if (resp.ok) {
          const data = await resp.json() as { requests?: unknown[] };
          setPendingCount(data.requests?.length ?? 0);
        } else {
          setPendingCount(0);
        }
      } catch {
        setPendingCount(0);
      }
    };
    void check();
    const interval = setInterval(() => void check(), CREATOR_REQUEST_BADGE_POLL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [canManageCreatorRequests]);

  const username = String(user?.username || user?.sub || emailLocalPart || t('common.operator'));
  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === '/prompt') {
      return location.pathname.startsWith('/prompt') || location.pathname.startsWith('/genius-loci');
    }
    return location.pathname.startsWith(path);
  };

  const operationsNav = [
    { name: t('common.dashboard'), href: '/dashboard', icon: Home },
    { name: t('common.experiences'), href: '/esperienze', icon: Globe },
    { name: t('common.interactions'), href: '/interazioni', icon: MessageCircle },
    { name: t('common.events'), href: '/events', icon: Calendar },
    { name: t('common.audioguides'), href: '/audioguide', icon: Headphones },
  ];

  const aiNav = [
    { name: t('common.analytics'), href: '/analytics', icon: BarChart3 },
    { name: t('header.profile'), href: '/profilo', icon: Building2 },
  ];

  const supportNav = [
    { name: t('header.oneri'), href: '/oneri', icon: Info },
    { name: t('common.documentation'), href: '/documentazione', icon: HelpCircle },
  ];

  const adminNav = canManageCreatorRequests ? [
    { name: 'APE™ L0-L6', href: '/superadmin/ape-config', icon: Layers },
    { name: t('common.categories'), href: '/superadmin/categorie', icon: Tags },
    { name: t('common.creator_requests'), href: '/creator-requests', icon: UserPlus, badge: pendingCount },
    { name: t('common.users'), href: '/superadmin/anagrafica-utenti', icon: Users },
    { name: t('common.organizations'), href: '/superadmin/organizzazioni-amministratori', icon: Building2 },
    { name: t('common.notifications'), href: '/superadmin/notifiche', icon: Bell },
  ] : [];

  return (
    <div
      className={`bg-white shadow-lg border-r border-border transition-all duration-300 fixed left-0 top-0 h-full z-50 flex flex-col ${
        isCollapsed ? 'w-16' : 'w-72'
      }`}
    >
      {/* Logo */}
      <div className="p-4 border-b border-border shrink-0">
        <div className="flex items-center justify-between">
          {!isCollapsed ? (
            <div className="flex items-center space-x-3">
              <img src="/glooci_logo.png" alt="Glooci" className="h-9 w-auto object-contain" />
              <div>
                <p className="font-bold text-text-primary text-sm leading-tight">Glooci Platform</p>
                <p className="text-xs text-text-secondary">Admin Console 3.0</p>
                <p className="text-[10px] text-text-secondary/80">Powered by APE 1.0</p>
              </div>
            </div>
          ) : (
            <img src="/glooci_logo.png" alt="Glooci" className="h-8 w-8 object-contain" />
          )}
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-text-secondary"
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-5">
        {/* Language selector in expanded sidebar */}
        {!isCollapsed && (
          <div className="px-2 py-1 bg-gray-50 rounded-lg flex items-center justify-between">
            <span className="text-xs text-text-secondary font-medium">{t('common.language')}:</span>
            <LanguageSelector showIcon={true} />
          </div>
        )}

        {/* Operatività */}
        <div>
          {!isCollapsed && (
            <p className="text-[10px] font-bold text-text-secondary/50 uppercase tracking-widest px-2 mb-1.5">
              {t('sidebar.operations')}
            </p>
          )}
          <div className="space-y-0.5">
            {operationsNav.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  title={isCollapsed ? item.name : undefined}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    active
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-text-secondary hover:text-text-primary hover:bg-gray-100'
                  }`}
                >
                  <Icon size={18} className="shrink-0" />
                  {!isCollapsed && <span className="truncate">{item.name}</span>}
                </Link>
              );
            })}
          </div>
        </div>

        {/* IA & Configurazione */}
        <div>
          {!isCollapsed && (
            <p className="text-[10px] font-bold text-text-secondary/50 uppercase tracking-widest px-2 mb-1.5">
              {t('sidebar.ai_engine')}
            </p>
          )}
          {isCollapsed && <div className="h-px bg-border mb-2" />}
          <div className="space-y-0.5">
            {aiNav.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  title={isCollapsed ? item.name : undefined}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    active
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-text-secondary hover:text-text-primary hover:bg-gray-100'
                  }`}
                >
                  <Icon size={18} className="shrink-0" />
                  {!isCollapsed && <span className="truncate">{item.name}</span>}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Supporto */}
        <div>
          {!isCollapsed && (
            <p className="text-[10px] font-bold text-text-secondary/50 uppercase tracking-widest px-2 mb-1.5">
              {t('sidebar.system')}
            </p>
          )}
          {isCollapsed && <div className="h-px bg-border mb-2" />}
          <div className="space-y-0.5">
            {supportNav.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  title={isCollapsed ? item.name : undefined}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    active
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-text-secondary hover:text-text-primary hover:bg-gray-100'
                  }`}
                >
                  <Icon size={18} className="shrink-0" />
                  {!isCollapsed && <span className="truncate">{item.name}</span>}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Super Admin */}
        {canManageCreatorRequests && adminNav.length > 0 && (
          <div>
            {!isCollapsed && (
              <p className="text-[10px] font-bold text-text-secondary/50 uppercase tracking-widest px-2 mb-1.5 flex items-center gap-1.5">
                <Shield size={10} /> Super Admin
              </p>
            )}
            {isCollapsed && <div className="h-px bg-border mb-2" />}
            <div className="space-y-0.5">
              {adminNav.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                const badgeCount = item.badge || 0;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    title={isCollapsed ? item.name : undefined}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all relative ${
                      active
                        ? 'bg-violet-600 text-white shadow-sm'
                        : 'text-violet-700 bg-violet-50 hover:bg-violet-100'
                    }`}
                  >
                    <Icon size={18} className="shrink-0" />
                    {!isCollapsed && (
                      <>
                        <span className="truncate flex-1">{item.name}</span>
                        {badgeCount > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                            {badgeCount > 9 ? '9+' : badgeCount}
                          </span>
                        )}
                      </>
                    )}
                    {isCollapsed && badgeCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {badgeCount > 9 ? '9+' : badgeCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* User area */}
      <div className="shrink-0 border-t border-border p-3 bg-gray-50">
        {!isCollapsed ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2.5 px-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${isSuperAdmin ? 'bg-violet-600' : 'bg-primary'}`}>
                {username.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-text-primary text-sm truncate">{username}</p>
                <p className="text-text-secondary text-xs flex items-center gap-1">
                  {isSuperAdmin ? (
                    <><Shield size={10} className="text-violet-600" /> Super Admin</>
                  ) : (
                    t('common.administrator')
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => void logout()}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-secondary hover:text-error hover:bg-error/5 rounded-lg transition-colors"
            >
              <LogOut size={16} />
              <span>{t('common.logout')}</span>
            </button>
          </div>
        ) : (
          <button
            onClick={() => void logout()}
            className="w-full p-2 text-text-secondary hover:text-error hover:bg-error/5 rounded-lg transition-colors flex items-center justify-center"
            title={t('common.logout')}
          >
            <LogOut size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
